import { isValidReactNode } from './isReactNode';

const isRegExp = (value: unknown): value is RegExp => {
    return value instanceof RegExp;
};
const isString = (value: unknown): value is string => {
    return typeof value === 'string';
};
const escapeRegExp = (string: string) => {
    const reRegExpChar = /[\\^$.*+?()[\]{}|]/g;
    const reHasRegExpChar = RegExp(reRegExpChar.source);

    return string && reHasRegExpChar.test(string) ? string.replace(reRegExpChar, '\\$&') : string;
};

/**
 * Given a string, replace every substring that is matched by the `match` regex
 * with the result of calling `fn` on matched substring. The result will be an
 * array with all odd indexed elements containing the replacements. The primary
 * use case is similar to using String.prototype.replace except for React.
 *
 * React will happily render an array as children of a react element, which
 * makes this approach very useful for tasks like surrounding certain text
 * within a string with react elements.
 *
 * Example:
 * matchReplace(
 *   'Emphasize all phone numbers like 884-555-4443.',
 *   /([\d|-]+)/g,
 *   (number, i) => <strong key={i}>{number}</strong>
 * );
 * // => ['Emphasize all phone numbers like ', <strong>884-555-4443</strong>, '.'
 *
 */
const replaceString = (
    str: React.ReactNode,
    match: string | RegExp,
    fn: (match: string, index: number, curCharStart: number) => React.ReactNode,
) => {
    let curCharStart = 0;
    let curCharLen = 0;

    if (str === '') {
        return [str];
    }
    if (!str || !isValidReactNode(str)) {
        throw new TypeError('First argument to react-string-replace#replaceString must be a string');
    }

    let re = match;

    if (!isRegExp(re)) {
        re = new RegExp(`(${escapeRegExp(re)})`, 'gi');
    }

    const result = String(str).split(re);
    const newResult: Array<string | React.ReactNode> = [...result];
    // Apply fn to all odd elements
    for (let i = 1, { length } = result; i < length; i += 2) {
        if (result[i] === undefined || result[i - 1] === undefined) {
            console.warn(
                'reactStringReplace: Encountered undefined value during string replacement. Your RegExp may not be working the way you expect.',
            );
            // eslint-disable-next-line no-continue
            continue;
        }

        curCharLen = result[i].length;
        curCharStart += result[i - 1].length;
        newResult[i] = fn(result[i], i, curCharStart);
        curCharStart += curCharLen;
    }

    return newResult;
};

export const reactStringReplace = (
    source: string | Array<React.ReactNode>,
    match: string | RegExp,
    fn: (match: string, index: number, curCharStart: number) => React.ReactNode,
) => {
    const sourceArray = Array.isArray(source) ? source : [source];

    return sourceArray.flatMap((str) => {
        return isString(str) ? replaceString(str, match, fn) : str;
    });
};
